import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Avatar } from '@/components/ui/avatar'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../shared/format'
import type { Meeting } from '../types'
import { useCrm } from '../ui'
import { ChannelLabel, ResultBadge } from './CrmBits'

type Props = { rows: Meeting[]; selectedId?: string | null; onSelect: (m: Meeting) => void; showCustomer?: boolean }

/** Görüşme kayıtları: dar alanda kart, genişte tablo. */
export function MeetingsTable({ rows, selectedId, onSelect, showCustomer }: Props) {
  const { t } = useTranslation()
  const { staffName, customerName } = useCrm()
  const next = (m: Meeting) =>
    m.nextAction ? (
      <span className="text-sm">
        <span className="block truncate">{m.nextAction.text}</span>
        <span className="block text-xs tabular-nums text-muted-foreground">
          {fmtDate(m.nextAction.date)} · {staffName(m.nextAction.staffId)}
        </span>
      </span>
    ) : (
      <span className="text-sm text-muted-foreground">—</span>
    )
  const staff = (m: Meeting) => (
    <span className="flex items-center gap-2 text-sm">
      <Avatar name={staffName(m.staffId)} className="size-7 [&>span]:text-[10px]" />
      <span className="truncate">{staffName(m.staffId)}</span>
    </span>
  )

  return (
    <div className="@container">
      <ul className="divide-y @4xl:hidden">
        {rows.map((m) => (
          <li key={m.id}>
            <button type="button" onClick={() => onSelect(m)} className={cn('w-full space-y-2 p-4 text-left transition-colors hover:bg-muted/40', selectedId === m.id && 'bg-primary/[0.06]')}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold tabular-nums">
                    {fmtDate(m.date)} · {m.start}
                  </p>
                  {showCustomer ? <p className="truncate text-sm">{customerName(m.customerId)}</p> : null}
                </div>
                <ResultBadge result={m.result} />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {staff(m)}
                <ChannelLabel channel={m.channel} />
              </div>
              <p className="line-clamp-2 text-sm text-muted-foreground">{m.summary}</p>
            </button>
          </li>
        ))}
      </ul>
      <div className="hidden @4xl:block">
        <Table>
          <THead>
            <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
              <TH>{t('crm.fields.date')}</TH>
              {showCustomer ? <TH>{t('crm.fields.customer')}</TH> : null}
              <TH>{t('crm.fields.staff')}</TH>
              <TH>{t('crm.fields.channel')}</TH>
              <TH>{t('crm.fields.result')}</TH>
              <TH>{t('crm.fields.summary')}</TH>
              <TH className="hidden @5xl:table-cell">{t('crm.fields.nextAction')}</TH>
              <TH className="w-8" aria-label={t('crm.cols.actions')} />
            </TR>
          </THead>
          <TBody>
            {rows.map((m) => (
              <TR key={m.id} onClick={() => onSelect(m)} className={cn('cursor-pointer', selectedId === m.id && 'bg-primary/[0.06] shadow-[inset_3px_0_0_var(--color-primary)]')}>
                <TD className="whitespace-nowrap">
                  <p className="font-medium tabular-nums">{fmtDate(m.date)}</p>
                  <p className="text-xs tabular-nums text-muted-foreground">{m.start}</p>
                </TD>
                {showCustomer ? (
                  <TD className="max-w-44">
                    <Link to={`/customers/${m.customerId}/meetings?m=${m.id}`} onClick={(e) => e.stopPropagation()} className="block truncate font-medium hover:text-primary">
                      {customerName(m.customerId)}
                    </Link>
                  </TD>
                ) : null}
                <TD className="max-w-40">{staff(m)}</TD>
                <TD>
                  <ChannelLabel channel={m.channel} />
                </TD>
                <TD>
                  <ResultBadge result={m.result} />
                </TD>
                <TD className="max-w-72">
                  <p className="line-clamp-2 text-sm">{m.summary}</p>
                </TD>
                <TD className="hidden max-w-52 @5xl:table-cell">{next(m)}</TD>
                <TD>
                  <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </div>
  )
}
